/**
 * Cache for operational folder subfolders to avoid redundant API calls
 * Uses the global cache utilities for consistency
 */

import {
  clearExpired,
  getCached,
  invalidateAllCache,
  invalidateCache,
  setCached,
} from './cacheUtils'

const CACHE_KEY_PREFIX = 'operational_subfolders_'
const CACHE_EXPIRY_MS = 5 * 60 * 1000 // 5 minutes

type Subfolder = {
  id: number
  name: string
  parentId: number | null
  icon?: string | null
  color?: string | null
  resortId: number
  [key: string]: unknown
}

type SubfoldersArray = Subfolder[]

/**
 * Get cached subfolders for a folder
 */
export const getCachedSubfolders = (
  folderId: number | null,
): SubfoldersArray | null => {
  return getCached<SubfoldersArray>(CACHE_KEY_PREFIX, folderId, CACHE_EXPIRY_MS)
}

/**
 * Set cached subfolders for a folder
 */
export const setCachedSubfolders = (
  folderId: number | null,
  subfolders: SubfoldersArray,
): void => {
  setCached(CACHE_KEY_PREFIX, folderId, subfolders, CACHE_EXPIRY_MS)
}

/**
 * Invalidate cache for a specific folder and optionally its parent
 */
export const invalidateSubfoldersCache = (
  folderId: number | null,
  invalidateParent: boolean = false,
): void => {
  invalidateCache(CACHE_KEY_PREFIX, folderId)

  if (invalidateParent) {
    invalidateAllSubfoldersCache()
  }
}

/**
 * Invalidate all subfolders cache
 */
export const invalidateAllSubfoldersCache = (): void => {
  invalidateAllCache(CACHE_KEY_PREFIX)
}

/**
 * Clear expired entries from cache
 */
export const clearExpiredCache = (): void => {
  clearExpired(CACHE_KEY_PREFIX, CACHE_EXPIRY_MS)
}
